import { formatMoney } from '@theme/money-formatting';

class FreeShippingGoal extends HTMLElement {
  constructor() {
    super();
    this.selectors = {
      leftToSpend: '[data-left-to-spend]',
    };
    this.goal = Number(this.dataset.minimumAmount) * Number(window.Shopify.currency.rate || 1) || 0;
    this.progress = this.querySelector('progress-bar');
    this.progressFill = this.querySelector('.progress-bar__fill');
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || /** @type {any} */ (window).theme?.currency || 'USD';
    this.onCartUpdate = this.onCartUpdate.bind(this);
  }

  connectedCallback() {
    this.updateShippingGoal(Number(this.dataset.cartTotal));
    document.addEventListener('cart:update', this.onCartUpdate);
    document.addEventListener('cart:updated', this.onCartUpdate);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:update', this.onCartUpdate);
    document.removeEventListener('cart:updated', this.onCartUpdate);
  }

  /** @param {Event} event */
  onCartUpdate(event) {
    const cartEvent = /** @type {CustomEvent<any>} */ (event);
    const cart = cartEvent.detail?.resource ?? cartEvent.detail?.cart;
    const nextAmount = cart?.items_subtotal_price ?? cart?.total_price;
    if (typeof nextAmount === 'number') {
      this.dataset.cartTotal = String(nextAmount);
      this.updateShippingGoal(nextAmount);
      return;
    }

    const cartItemsComponent = /** @type {HTMLElement | null} */ (this.closest('cart-items-component'));
    const sectionId = cartItemsComponent?.dataset.sectionId;
    const sections = cartEvent.detail?.data?.sections;
    const sectionMarkup = sectionId && sections ? sections[sectionId] : null;
    if (sectionMarkup) {
      const newSectionHTML = new DOMParser().parseFromString(sectionMarkup, 'text/html');
      const nextGoalElement =
        newSectionHTML.querySelector(`free-shipping-goal[data-minimum-amount="${this.dataset.minimumAmount}"]`) ||
        newSectionHTML.querySelector('free-shipping-goal');
      const nextCartTotal = Number(nextGoalElement?.getAttribute('data-cart-total'));
      if (Number.isFinite(nextCartTotal)) {
        this.dataset.cartTotal = String(nextCartTotal);
        this.updateShippingGoal(nextCartTotal);
        return;
      }
    }

    requestAnimationFrame(() => {
      this.updateShippingGoal(Number(this.dataset.cartTotal || 0));
    });
  }

  /** @param {number} amount */
  updateShippingGoal(amount) {
    if (!this.goal || !(this.progress instanceof HTMLElement)) return;
    if (!(this.progressFill instanceof HTMLElement)) {
      this.progressFill = this.querySelector('.progress-bar__fill');
    }

    if (amount > 0) {
      this.classList.remove('hidden');
    } else {
      this.classList.add('hidden');
    }

    this.cartTotal = amount / 100;
    this.goalLeft = this.goal - this.cartTotal;
    this.goalDone = this.goalLeft <= 0;

    this.percent = (this.cartTotal * 100) / this.goal;

    if (!Number.isFinite(this.percent)) this.percent = 0;
    if (this.percent >= 100) this.percent = 100;
    if (this.percent < 0) this.percent = 0;

    const percentValue = `${this.percent}%`;
    if (this.progressFill instanceof HTMLElement) {
      this.progressFill.style.width = percentValue;
    }

    if (this.cartTotal >= this.goal) {
      this.classList.add('free-shipping-goal--done');
      this.progress.dataset.value = String(this.cartTotal);
      this.progress.dataset.max = String(this.goal);
    } else {
      let spend = (this.goal - this.cartTotal) * 100;
      const leftToSpendElement = this.querySelector(this.selectors.leftToSpend);
      if (leftToSpendElement) {
        leftToSpendElement.innerHTML = formatMoney(spend, this.moneyFormat, this.currency);
      }
      this.classList.remove('free-shipping-goal--done');
      this.progress.dataset.value = String(this.cartTotal);
      this.progress.dataset.max = String(this.goal);
    }
  }
}
customElements.define('free-shipping-goal', FreeShippingGoal);
